import { AlertCircle, ArrowRight, BarChart3, CalendarDays, QrCode, ReceiptText } from "lucide-react";
import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAdminAuth } from "../../../context/AdminAuthContext";

const ADMIN_ACCESS_POINTS = [
  { icon: BarChart3, label: "Live revenue", value: "Sales pulse", tint: "text-aan-bright" },
  { icon: CalendarDays, label: "Event inventory", value: "Tier control", tint: "text-[var(--pp-gold)]" },
  { icon: ReceiptText, label: "Order activity", value: "Buyer tracking", tint: "text-[var(--pp-salmon)]" },
  { icon: QrCode, label: "Gate scanner", value: "Fast check-in", tint: "text-aan-bright" },
];

export default function AdminLoginPage() {
  const { admin, signIn } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (admin) {
    return <Navigate to="/admin" replace />;
  }

  const nextPath = (location.state as { from?: string } | null)?.from ?? "/admin";

  return (
    <div className="ego-shell relative min-h-screen overflow-hidden bg-black">
      <div className="absolute inset-x-0 top-0 z-20 h-1 bg-aan-green" />
      <div className="grid min-h-screen w-full lg:grid-cols-[1.08fr_minmax(420px,560px)]">
        <div className="relative hidden overflow-hidden p-10 text-white lg:flex lg:flex-col lg:p-14">
          <img
            src="/assets/hero-bg.jpeg"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full select-none object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black via-black/[0.82] to-black/[0.58]" />

          <div className="relative flex items-center justify-between gap-6">
            <img src="/assets/logo-white.png" alt="African Art Network" className="w-52" />
            <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2 font-display text-[11px] font-black uppercase tracking-[0.22em] text-aan-bright">
              Admin access
            </span>
          </div>

          <div className="relative mt-auto max-w-3xl">
            <div className="font-display text-xs font-black uppercase tracking-[0.32em] text-aan-bright">
              AAN operations
            </div>
            <h1 className="mt-5 max-w-2xl font-display text-6xl font-black uppercase leading-none text-white">
              Run the box office without leaving the AAN experience.
            </h1>
            <p className="mt-5 max-w-xl text-sm leading-8 text-white/[0.72]">
              Manage events, orders, payments, registrations, and QR check-in from the same visual language customers see on the public site.
            </p>

            <div className="mt-10 grid gap-3 sm:grid-cols-2">
              {ADMIN_ACCESS_POINTS.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="rounded-[8px] border border-white/15 bg-black/45 p-4 backdrop-blur">
                    <Icon className={item.tint} size={22} />
                    <div className="mt-4 font-display text-base font-black uppercase text-white">{item.label}</div>
                    <div className="mt-1 text-xs text-white/[0.56]">{item.value}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="relative z-10 flex items-center justify-center px-4 py-10 sm:px-8 lg:px-10">
          <form
            className="ego-surface w-full max-w-md overflow-hidden p-7 sm:p-9"
            onSubmit={async (submitEvent) => {
              submitEvent.preventDefault();
              setIsSubmitting(true);
              setError(null);
              try {
                await signIn(credentials.email, credentials.password);
                navigate(nextPath, { replace: true });
              } catch (requestError) {
                setError(requestError instanceof Error ? requestError.message : "Login failed");
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            <div className="mb-8 lg:hidden">
              <img src="/assets/logo-white.png" alt="African Art Network" className="w-44" />
            </div>

            <span className="ego-badge ego-badge-brand">Admin sign in</span>
            <h2 className="mt-4 font-display text-4xl font-black uppercase leading-none text-foreground">
              Welcome back
            </h2>
            <p className="mt-3 text-sm leading-7 text-muted-foreground">
              Enter your credentials to manage African Art Network ticketing operations.
            </p>

            <div className="mt-8 space-y-4">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-muted-foreground">Email</span>
                <input
                  type="email"
                  value={credentials.email}
                  onChange={(event) =>
                    setCredentials((current) => ({ ...current, email: event.target.value }))
                  }
                  className="ego-input"
                  required
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-muted-foreground">Password</span>
                <input
                  type="password"
                  value={credentials.password}
                  onChange={(event) =>
                    setCredentials((current) => ({ ...current, password: event.target.value }))
                  }
                  className="ego-input"
                  required
                />
              </label>
            </div>

            {error && (
              <div className="mt-5 flex items-start gap-2 rounded-[8px] border border-[rgba(235,52,67,0.35)] bg-[rgba(235,52,67,0.12)] px-4 py-3 text-sm text-[#ff9aa3]">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button type="submit" disabled={isSubmitting} className="ego-btn-primary mt-6 w-full !py-3">
              {isSubmitting ? "Signing in..." : "Enter admin"}
              {!isSubmitting && <ArrowRight size={16} />}
            </button>

            <p className="mt-6 text-center text-xs text-white/[0.44]">
              Protected workspace for verified AAN administrators.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
